import { Check, Plus, Upload } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { Link } from '@/components/ui/Link'
import { AddCertificateHolderModal } from './AddCertificateHolderModal'
import { CoiHoldersIllustration } from './CoiHoldersIllustration'

enum HolderModal {
  HOLDER = 'holder',
  NAMED_PARTY = 'named_party',
}

/** Optional: line up COI holders, loss payees and additional insureds so certificates go out on binding. */
export function CoiHoldersStep({
  id,
  canAdvance,
  onDone,
}: {
  id: string
  /** False while the step after this one is still locked behind earlier steps. */
  canAdvance: boolean
  onDone: () => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  // Kept after closing so the dialog's title doesn't swap mid-close.
  const [modal, setModal] = useState(HolderModal.HOLDER)
  const [added, setAdded] = useState<string[]>([])

  function open(kind: HolderModal) {
    setModal(kind)
    setIsOpen(true)
  }

  function add(entry: string) {
    setAdded((current) => [...current, entry])
  }

  return (
    <div className="mt-3 flex flex-col items-start gap-3">
      <p className="text-ink-500 text-sm lg:max-w-[400px]">
        Add the customers who need a certificate so we can send them out as soon as your coverage is active.
      </p>
      <CoiHoldersIllustration />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => open(HolderModal.HOLDER)}>
          <Plus />
          Add COI holder
        </Button>
        <FileUpload
          className="flex"
          accept=".csv,.xlsx,.pdf"
          onChange={(file) => {
            if (file instanceof File) add(file.name)
          }}
        >
          <Button variant="outline" size="sm">
            <Upload />
            Upload list
          </Button>
        </FileUpload>
      </div>
      <div className="border-ink-200 w-full border-t border-dashed" />
      <div className="flex flex-col items-start gap-2">
        <span className="text-ink-400 text-xs font-medium">Less common</span>
        <Link
          kind="util"
          emphasis
          icon={<Plus />}
          href="#"
          role="button"
          onClick={(event) => {
            event.preventDefault()
            open(HolderModal.NAMED_PARTY)
          }}
        >
          Add loss payee or additional insured
        </Link>
      </div>
      {added.length > 0 && (
        <ul className="flex flex-col gap-1">
          {added.map((entry, index) => (
            <li key={index} className="text-ink-500 flex items-center gap-1 text-sm">
              <Check className="size-4 shrink-0 text-green-600" />
              {entry}
            </li>
          ))}
        </ul>
      )}
      <Button disabled={!canAdvance} onClick={onDone}>
        Next step
      </Button>
      <AddCertificateHolderModal
        id={`${id}-${modal}`}
        isNamedParty={modal === HolderModal.NAMED_PARTY}
        open={isOpen}
        onOpenChange={setIsOpen}
        onSave={() => {
          add(modal === HolderModal.NAMED_PARTY ? 'Loss payee or additional insured' : 'Certificate holder')
          setIsOpen(false)
        }}
      />
    </div>
  )
}
